import type { Table } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@/components/data-table/data-table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { MinimumOrderProgress } from '@/features/lot-sizing/component/lot-sizing-supplier-minimum-order-progress';
import { useLotSizingDraftPreview } from '@/features/lot-sizing/hooks/use-lot-sizing-draft-preview';
import { useLotSizingSupplierTable } from '@/features/lot-sizing/hooks/use-lot-sizing-supplier-table';
import { useScrollIntoViewWhenOpen } from '@/hooks/use-scroll-into-view-when-open';
import { cn } from '@/lib/utils';
import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';
import type { Supplier } from '@/types/core/purchase-planning/supplier';

type LotSizingSupplierTableProps = {
  defaultOpen: boolean;
  hasRelatedPurchaseOrders: boolean;
  table: Table<LotSizingLine>;
  lotSizingId: string;
  supplier: Supplier;
};

export function LotSizingSupplierTable({
  defaultOpen,
  hasRelatedPurchaseOrders,
  table: masterTable,
  lotSizingId,
  supplier,
}: LotSizingSupplierTableProps) {
  const { t } = useTranslation('lotSizing');
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useScrollIntoViewWhenOpen(isOpen);

  const { table, total, hasMore, isFetching, loadMoreRef } =
    useLotSizingSupplierTable(
      masterTable,
      lotSizingId,
      supplier,
      hasRelatedPurchaseOrders,
    );
  const { supplierAmounts } = useLotSizingDraftPreview(lotSizingId);
  const currentLotSizingAmount =
    supplierAmounts.get(String(supplier.id)) ?? supplier.currentLotSizingAmount;

  const minOrderAmount = supplier.minOrderAmount;

  return (
    <Collapsible
      className='rounded-md border bg-background'
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <CollapsibleTrigger className='flex w-full flex-col gap-2 p-4 text-left'>
        <div className='flex w-full flex-row justify-between gap-3'>
          <div className='flex flex-row items-center gap-3'>
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                isOpen && 'rotate-180',
              )}
            />
            <div className='flex flex-row items-center gap-3'>
              <div className='font-medium text-sm'>
                {supplier.name ?? supplier.id}
              </div>
              <div className='text-muted-foreground text-xs'>
                {t('refsTotal', { total })}
              </div>
            </div>
          </div>
          <div className='flex flex-row items-center gap-3'>
            <span className='text-muted-foreground text-xs'>
              {t('leadTimeValue', {
                value:
                  (supplier.leadTimeProductionWeeks ?? 0) +
                    (supplier.leadTimeDeliveryWeeks ?? 0) || '-',
              })}
            </span>
            <span className='text-muted-foreground text-xs'>
              {t('shippingTimeValue', {
                value: supplier.leadTimeDeliveryWeeks ?? '-',
              })}
            </span>
            <span className='text-muted-foreground text-xs'>
              {t('ProductionTimeValue', {
                value: supplier.leadTimeProductionWeeks ?? '-',
              })}
            </span>
          </div>
        </div>
        {minOrderAmount > 0 && (
          <MinimumOrderProgress
            className='pl-7'
            currency={supplier.currency ?? undefined}
            currentAmount={currentLotSizingAmount}
            minimumAmount={minOrderAmount}
          />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className='px-4 pb-4'>
        <div className='max-h-[calc(100vh-16rem)]' ref={contentRef}>
          <DataTable
            bodyFooter={
              <div
                className='py-3 text-center text-muted-foreground text-sm'
                ref={loadMoreRef}
              >
                {isFetching
                  ? t('loading')
                  : hasMore
                    ? t('scrollToLoadMore')
                    : t('endOfList')}
              </div>
            }
            className='max-h-[calc(100vh-16rem)]'
            showPagination={false}
            table={table}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
